import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import { AlertDialog, Badge, useToast } from '@ntconsult/aluma-react';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtivarCampanha, useCampanhaAtiva, useInvalidarFase } from '../hooks';
import type { Campanha } from '../types/Campanha';
import type { Progresso } from '../types/Progresso';
import { ResumoAbrir } from './ResumoAbrir';

type Outra = { id: string; nome: string };

/**
 * Abrir / Reabrir (spec 28 R18). Com outra ativa, a única saída é a troca atômica
 * ("Pausar {{outra}} e abrir esta"); nunca desativar e ativar em dois passos.
 */
export function AbrirCampanhaDialog({
  campanha,
  progresso,
  open,
  onClose,
  onAberta,
}: {
  campanha: Campanha;
  progresso: Progresso;
  open: boolean;
  onClose: () => void;
  onAberta: () => void;
}) {
  const { t } = useTranslation(['campanhas', 'participantes', 'common', 'erros', 'convites']);
  const { toast } = useToast();
  const invalidarFase = useInvalidarFase(campanha.id);
  const ativa = useCampanhaAtiva(open);
  const ativar = useAtivarCampanha(campanha.id);
  // O 409 traz quem bloqueou agora; vale mais que a leitura feita ao abrir o diálogo.
  const [conflito, setConflito] = useState<Outra | null>(null);
  const [emRodada, setEmRodada] = useState(false);
  const lida = ativa.data && ativa.data.id !== campanha.id ? ativa.data : null;
  const outra = conflito ?? lida;

  function fechar() {
    setConflito(null);
    setEmRodada(false);
    onClose();
  }

  async function confirmar(e: React.MouseEvent) {
    e.preventDefault(); // o diálogo só fecha quando a resposta chega
    try {
      await ativar.mutateAsync(outra?.id);
      fechar();
      onAberta();
    } catch (error) {
      const codigo = mensagemDeErro(error);
      const bloqueou = isAxiosError(error) ? error.response?.data?.campanhaAtiva : undefined;
      if (codigo === 'OUTRA_CAMPANHA_ATIVA' && bloqueou) return setConflito(bloqueou);
      if (codigo === 'RODADA_EM_ANDAMENTO') {
        if (bloqueou) setConflito(bloqueou);
        return setEmRodada(true);
      }
      fechar();
      if (codigo === 'CAMPANHA_TERMINADA') {
        void invalidarFase();
        return;
      }
      toast.danger({ title: t(`erros:${codigo}`) });
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && fechar()}>
      <AlertDialog.Content>
        <AlertDialog.Title>
          {t('campanhas:dialogo.abrir', { nome: campanha.nome })}
        </AlertDialog.Title>
        <AlertDialog.Description>{t('participantes:confirmarAtivacao')}</AlertDialog.Description>
        <ResumoAbrir campanha={campanha} progresso={progresso} />
        {outra && (
          <p className="flex flex-wrap items-center gap-2 text-sm" data-outra-ativa>
            {t('campanhas:dialogo.outraAtiva', { outra: outra.nome })}
            {emRodada && <Badge tone="info">{t('convites:rodada.status.em_andamento')}</Badge>}
          </p>
        )}
        <AlertDialog.Footer>
          <AlertDialog.Cancel variant="secondary">{t('common:cancel')}</AlertDialog.Cancel>
          {!emRodada && !ativa.isLoading && (
            <AlertDialog.Action variant="primary" loading={ativar.isPending} onClick={confirmar}>
              {outra
                ? t('campanhas:acoes.desativarEAbrir', { outra: outra.nome })
                : t('campanhas:acoes.abrir')}
            </AlertDialog.Action>
          )}
        </AlertDialog.Footer>
      </AlertDialog.Content>
    </AlertDialog>
  );
}
